import classNames from 'classnames';
import * as React from 'react';
import type { FormFieldProps } from '../FormFieldProps';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { Theme } from '~/Utils/Theme';

export interface FormFieldTypeTextAreaProps
    extends FormFieldProps<string>,
        Omit<React.InputHTMLAttributes<HTMLTextAreaElement>, keyof FormFieldProps<string>> {
    label?: string | React.ReactChild;
    placeholder?: string;
    className?: string;
    ariaDescribedBy?: string;
    theme?: Theme;
    autoGrow?: boolean;
    showCharacterCounter?: boolean;
}

export const FormFieldTypeTextArea: React.FC<React.PropsWithChildren<FormFieldTypeTextAreaProps>> = React.memo(
    props => {
        const contextualTheme = useTheme();

        const {
            label,
            errorMessage,
            className,
            required,
            ariaDescribedBy,
            placeholder,
            showErrorMessage = false,
            theme = contextualTheme,
            autoGrow = false,
            showCharacterCounter = false,
            maxLength,
            ...inputProps // name, onChange, onBlur, value
        } = props;

        const { withRoundedCorners } = theme;
        const ref = React.useRef<HTMLTextAreaElement>(null);

        React.useEffect(() => {
            if (ref.current && autoGrow) {
                ref.current.style.height = 'auto';
                const scrollHeight = ref.current.scrollHeight;
                ref.current.style.height = scrollHeight + 26 + 'px';
            }
        }, [autoGrow, inputProps.value]);

        const textareaValue = inputProps.value || '';
        const charactersLeft = maxLength ? maxLength - textareaValue.length : 0;

        return (
            <div
                className={classNames(
                    'Form__field',
                    'Form__fieldTextArea',
                    className,
                    showErrorMessage && 'Form__fieldError',
                    withRoundedCorners && 'Form__fieldRounded',
                    autoGrow && 'Form__fieldTextArea--autoGrow'
                )}
            >
                <label>
                    {label && (
                        <div className="Form__label">
                            <span>{label}</span>
                        </div>
                    )}
                    <textarea
                        ref={ref}
                        required={required}
                        maxLength={maxLength}
                        {...inputProps}
                        onChange={e => inputProps.onChange(e.target.value)}
                        onBlur={e => {
                            inputProps.onBlur && inputProps.onBlur(e);
                            inputProps.value && inputProps.onChange(inputProps.value.trim());
                        }}
                        aria-describedby={ariaDescribedBy}
                        placeholder={placeholder}
                    />
                    {showCharacterCounter && maxLength && (
                        <span className="Form__fieldTextAreaCounter">
                            {charactersLeft}/{maxLength}
                        </span>
                    )}
                    {showErrorMessage && <span className="Form__errorMessage">{errorMessage}</span>}
                </label>
            </div>
        );
    }
);
